import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, combineLatest, distinctUntilChanged, expand, forkJoin, map, reduce, skip, switchMap, tap } from 'rxjs';
import { GroupService } from './group.service';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';
import { UserService } from './user.service';
import { showsAllPractices } from '../utils/calendar-practices';
import { environment } from 'src/environments/environment';
import * as moment from 'moment';

export interface PaginationInfo {
  totalCount: number;
  currentPage: number;
  totalPages: number;
  limit: number;
  hasMore: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationInfo;
}

/**
 * §3b decision 2 — "both renderers stay, and the user picks".
 *
 * The Netcare booking form and the VeriClaim day list serve different audiences: one is faxed to
 * a hospital to book a slate, the other is the surgeon's own running order. Neither is a superset
 * of the other, so the choice is the user's rather than a default.
 */
/**
 * §3f (2026-09-01) removed `'daylist'` with the day-sheet export; 2026-09-04 brought it back as
 * the PDF day sheet on the practice letterhead. The server validates against its own
 * `DAY_EXPORT_DOCUMENT_TYPES` and 400s anything else, so this type narrowing is a convenience
 * for the caller, not the enforcement.
 *
 *   - `'netcare'` — the Theatre List, a `.docx` the hospital's bookings office completes.
 *   - `'daylist'` — the Day Sheet, a `.pdf` running order for the team on the day.
 */
export type TheatreListDocumentType = 'netcare' | 'daylist';

/** The extension each document type downloads with — the fallback when the server's name is unreadable. */
export const THEATRE_LIST_DOCUMENT_EXTENSIONS: Record<TheatreListDocumentType, string> = {
  netcare: '.docx',
  daylist: '.pdf',
};

/** The month list while a newly selected practice's month is loading, so watchers can tell it from an empty month. */
export const MONTH_LOADING: any[] = [];

@Injectable({
  providedIn: 'root'
})
export class PatientAppointmentService {
  private apiUrl = `api/appointments`;
  private patientAppointmentsSubject = new BehaviorSubject<any[] | null>(null);
  private editingAppointmentSubject = new BehaviorSubject<any>(null);
  private upcomingAppointmentSubject = new BehaviorSubject<any[] | null>(null);
  private monthAppointmentSubject = new BehaviorSubject<any[] | null>(null);
  private selectedMonthSubject = new BehaviorSubject<{ year: number, month: number } | null>(null);

  // Pagination subjects
  private upcomingPaginationSubject = new BehaviorSubject<PaginationInfo | null>(null);
  private isLoadingMoreSubject = new BehaviorSubject<boolean>(false);

  patientAppointments$ = this.patientAppointmentsSubject.asObservable();
  editingAppointment$ = this.editingAppointmentSubject.asObservable();
  upcomingAppointments$ = this.upcomingAppointmentSubject.asObservable();
  monthAppointments$ = this.monthAppointmentSubject.asObservable();
  selectedMonth$ = this.selectedMonthSubject.asObservable();
  upcomingPagination$ = this.upcomingPaginationSubject.asObservable();
  isLoadingMore$ = this.isLoadingMoreSubject.asObservable();

  groupId: any;
  /** A doctor showing appointments from every practice they belong to, not just the selected one. */
  private allPractices = false;
  private currentPage = 1;
  private pageSize = 50;

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService,
    private groupService: GroupService,
    private userService: UserService,
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();

    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          this.currentPage = 1; // Reset page when group changes
          this.upcomingAppointmentSubject.next([]);
          this.monthAppointmentSubject.next(MONTH_LOADING);
          return this.loadGroupAppointments();
        }
        return EMPTY;
      })
    ).subscribe();

    combineLatest([this.userService.userProfile$, this.groupService.userGroups$]).pipe(
      map(([profile, groups]) => showsAllPractices(profile, groups)),
      distinctUntilChanged(),
      tap(allPractices => (this.allPractices = allPractices)),
      skip(1),
      switchMap(() => this.loadGroupAppointments()),
    ).subscribe();
  }

  /** Adds allPractices=1 when the calendar shows every practice; the server decides which practices. */
  private withPractices(params: HttpParams): HttpParams {
    return this.allPractices ? params.set('allPractices', '1') : params;
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          this.patientAppointmentsSubject.next([]);
          return this.loadAppointmentsByPatient(patient.id);
        } else {
          this.patientAppointmentsSubject.next([]);
          return [];
        }
      }),
      tap((results: any) => {
        const appointments = results ? results.data : [];
        this.patientAppointmentsSubject.next(appointments);
      })
    ).subscribe();
  }

  private setupSocketListeners(): void {
    let currentPatientId: string | null = null;

    this.patientService.currentPatient$.subscribe(patient => {
      currentPatientId = patient ? patient.id : null;
    });

    this.socketService.listen('appointmentAdded', 'patient-appointment-service').subscribe(appointment => {
      if (appointment.patientId === currentPatientId) {
        const currentAppointments = this.patientAppointmentsSubject.getValue() || [];
        // Guard against the same appointment being appended twice (duplicate/replayed broadcast).
        if (!currentAppointments.some(a => a.id === appointment.id)) {
          this.patientAppointmentsSubject.next([...currentAppointments, appointment]);
        }
      }
      this.insertAppointmentInCorrectPosition(appointment);
      this.addToMonthAppointments(appointment);
    });

    this.socketService.listen('appointmentUpdated', 'patient-appointment-service').subscribe(updatedAppointment => {
      if (updatedAppointment.patientId === currentPatientId) {
        const currentAppointments = this.patientAppointmentsSubject.getValue();
        const updatedAppointments = currentAppointments!.map(appointment =>
          appointment.id === updatedAppointment.id ? updatedAppointment : appointment
        );
        this.patientAppointmentsSubject.next(updatedAppointments);
      }
      this.updateUpcomingAppointments(updatedAppointment);
      this.updateMonthAppointments(updatedAppointment);
    });

    this.socketService.listen('appointmentDeleted', 'patient-appointment-service').subscribe(deletedAppointment => {
      if (deletedAppointment.patientId === currentPatientId) {
        const currentAppointments = this.patientAppointmentsSubject.getValue();
        const updatedAppointments = currentAppointments!.filter(appointment => appointment.id !== deletedAppointment.id);
        this.patientAppointmentsSubject.next(updatedAppointments);
      }
      this.removeFromUpcomingAppointments(deletedAppointment.id);
      this.removeFromMonthAppointments(deletedAppointment.id);
    });

    this.socketService.listen('patientUpdated', 'patient-appointment-service').subscribe((updatedPatient) => {
      const currentUpcoming = this.upcomingAppointmentSubject.getValue();
      const currentMonth = this.monthAppointmentSubject.getValue();

      const updatedUpcoming = currentUpcoming?.map(item => {
        if (item.Patient.id === updatedPatient.id) {
          return {
            ...item,
            Patient: {
              ...item.Patient,
              name: updatedPatient.name,
              surname: updatedPatient.surname,
              diagnosis: updatedPatient.diagnosis,
              patientNumber: updatedPatient.patientNumber,
              requiresValidation: updatedPatient.requiresValidation,
              requiresICDValidation: updatedPatient.requiresICDValidation,
              wasSelfBoarded: updatedPatient.wasSelfBoarded,
              reasonForVisiting: updatedPatient.reasonForVisiting,
              status: updatedPatient.status,
            }
          };
        }
        return item;
      });

      const updatedMonth = currentMonth?.map(item => {
        if (item.Patient.id === updatedPatient.id) {
          return {
            ...item,
            Patient: {
              ...item.Patient,
              name: updatedPatient.name,
              surname: updatedPatient.surname,
              diagnosis: updatedPatient.diagnosis,
              patientNumber: updatedPatient.patientNumber,
              requiresValidation: updatedPatient.requiresValidation,
              requiresICDValidation: updatedPatient.requiresICDValidation,
              wasSelfBoarded: updatedPatient.wasSelfBoarded,
              reasonForVisiting: updatedPatient.reasonForVisiting,
              status: updatedPatient.status,
            }
          };
        }
        return item;
      });

      if (updatedUpcoming) {
        this.upcomingAppointmentSubject.next(updatedUpcoming);
      }

      if (updatedMonth) {
        this.monthAppointmentSubject.next(updatedMonth);
      }
    });
  }

  private insertAppointmentInCorrectPosition(appointment: any) {
    const currentUpcoming = this.upcomingAppointmentSubject.getValue() || [];
    if (currentUpcoming.some(a => a.id === appointment.id)) {
      return;
    }
    const appointmentDate = new Date(appointment.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Only insert if appointment is in the future
    if (appointmentDate >= today) {
      // Check if we should add it to the current loaded data
      const pagination = this.upcomingPaginationSubject.getValue();
      if (pagination && currentUpcoming.length >= pagination.limit) {
        // We're at capacity, need to check if this appointment should be visible
        const lastAppointment = currentUpcoming[currentUpcoming.length - 1];
        if (new Date(appointment.date) > new Date(lastAppointment.date)) {
          // This appointment is after our current view, don't add it
          return;
        }
      }

      // Find the correct position to insert
      let insertIndex = currentUpcoming.findIndex(app =>
        new Date(app.date) > appointmentDate ||
        (new Date(app.date).getTime() === appointmentDate.getTime() && app.timeSlot > appointment.timeSlot)
      );

      if (insertIndex === -1) {
        insertIndex = currentUpcoming.length;
      }

      const updatedUpcoming = [...currentUpcoming];
      updatedUpcoming.splice(insertIndex, 0, appointment);

      // If we're over the limit, remove the last item
      if (pagination && updatedUpcoming.length > pagination.limit) {
        updatedUpcoming.pop();
      }

      this.upcomingAppointmentSubject.next(updatedUpcoming);
    }
  }

  private updateUpcomingAppointments(appointment: any) {
    const currentUpcomingAppointments = this.upcomingAppointmentSubject.getValue();
    const existingAppointmentIndex = currentUpcomingAppointments!.findIndex(
      (app) => app.id === appointment.id
    );

    if (existingAppointmentIndex !== -1) {
      const updatedUpcomingAppointments = [...currentUpcomingAppointments!];
      updatedUpcomingAppointments[existingAppointmentIndex] = appointment;
      this.upcomingAppointmentSubject.next(updatedUpcomingAppointments);
    }
  }

  private updateMonthAppointments(appointment: any) {
    const selectedMonth = this.selectedMonthSubject.getValue();
    if (selectedMonth) {
      const appointmentDate = new Date(appointment.date);
      if (
        appointmentDate.getFullYear() === selectedMonth.year &&
        appointmentDate.getMonth() + 1 === selectedMonth.month
      ) {
        const currentMonthAppointments = this.monthAppointmentSubject.getValue() || [];
        const existingAppointmentIndex = currentMonthAppointments.findIndex(
          (app) => app.id === appointment.id
        );

        if (existingAppointmentIndex !== -1) {
          const updatedMonthAppointments = [...currentMonthAppointments];
          updatedMonthAppointments[existingAppointmentIndex] = appointment;
          this.monthAppointmentSubject.next(updatedMonthAppointments);
        } else {
          const updatedMonthAppointments = [...currentMonthAppointments, appointment];
          this.monthAppointmentSubject.next(updatedMonthAppointments);
        }
      }
    }
  }

  private addToMonthAppointments(appointment: any) {
    const selectedMonth = this.selectedMonthSubject.getValue();
    if (selectedMonth) {
      const appointmentDate = new Date(appointment.date);
      if (
        appointmentDate.getFullYear() === selectedMonth.year &&
        appointmentDate.getMonth() + 1 === selectedMonth.month
      ) {
        const currentMonthAppointments = this.monthAppointmentSubject.getValue() || [];
        const existingAppointmentIndex = currentMonthAppointments.findIndex(
          (app) => app.id === appointment.id
        );

        if (existingAppointmentIndex === -1) {
          const updatedMonthAppointments = [...currentMonthAppointments, appointment];
          this.monthAppointmentSubject.next(updatedMonthAppointments);
        }
      }
    }
  }

  private removeFromUpcomingAppointments(appointmentId: string) {
    const currentUpcomingAppointments = this.upcomingAppointmentSubject.getValue();
    const updatedUpcomingAppointments = currentUpcomingAppointments!.filter(appointment => appointment.id !== appointmentId);
    this.upcomingAppointmentSubject.next(updatedUpcomingAppointments);
  }

  private removeFromMonthAppointments(appointmentId: string) {
    const currentMonthAppointments = this.monthAppointmentSubject.getValue() || [];
    const updatedMonthAppointments = currentMonthAppointments.filter(appointment => appointment.id !== appointmentId);
    this.monthAppointmentSubject.next(updatedMonthAppointments);
  }

  /* loadGroupAppointments(): Observable<any> {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const groupId = this.groupId;

    if (!groupId) {
      console.error('groupId is not set.');
      return EMPTY;
    }

    return forkJoin({
      monthAppointments: this.loadAppointmentsByMonth(year, month),
      upcomingAppointments: this.loadAppointmentsForTodayAndUpcoming(groupId, 1, this.pageSize)
    }).pipe(
      tap(results => {
        this.monthAppointmentSubject.next(results.monthAppointments.data || []);
        this.upcomingAppointmentSubject.next(results.upcomingAppointments.data || []);
        this.upcomingPaginationSubject.next(results.upcomingAppointments.pagination || null);
      })
    );
  } */

  loadGroupAppointments(): Observable<any> {
    // The month the calendar is on, so a practice switch does not jump it back to this month.
    const now = new Date();
    const viewed = this.selectedMonthSubject.getValue();
    const year = viewed?.year ?? now.getFullYear();
    const month = viewed?.month ?? now.getMonth() + 1;

    const groupId = this.groupId;
    if (!groupId) {
      return EMPTY;
    }
    return forkJoin({
      monthAppointments: this.loadAppointmentsByMonth(year, month),
      upcomingPage: this.loadAppointmentsForTodayAndUpcoming(groupId, 1, this.pageSize)
    }).pipe(
      tap(({ monthAppointments, upcomingPage }) => {
        this.monthAppointmentSubject.next(monthAppointments.data || []);
        // now we’ve got _all_ upcoming pages concatenated
        this.upcomingAppointmentSubject.next(upcomingPage.data || []);

        this.upcomingPaginationSubject.next(upcomingPage.pagination || null);
      })
    );
  }

  hasMoreUpcomingAppointments(): boolean {
    const p = this.upcomingPaginationSubject.getValue();
    return !!(p?.hasMore);
  }

  /**
   * Load next page of upcoming appointments and append (for schedule-list "Load more").
   */
  loadMoreUpcomingAppointments(): Observable<PaginatedResponse<any>> {
    const groupId = this.groupId;
    const pagination = this.upcomingPaginationSubject.getValue();
    if (!groupId || !pagination?.hasMore) {
      return EMPTY;
    }
    this.isLoadingMoreSubject.next(true);
    const nextPage = (pagination.currentPage || 1) + 1;
    return this.loadAppointmentsForTodayAndUpcoming(groupId, nextPage, this.pageSize).pipe(
      tap((resp) => {
        const current = this.upcomingAppointmentSubject.getValue() || [];
        this.upcomingAppointmentSubject.next([...current, ...(resp.data || [])]);
        this.upcomingPaginationSubject.next(resp.pagination || null);
        this.isLoadingMoreSubject.next(false);
      })
    );
  }

  /**
   * @param options.skipSharedMonthCache When true, only fetches data and does not write
   * `monthAppointments$` / selected month (used by the calendar for the second month a week runs into).
   */
  loadAppointmentsByMonth(
    year: number,
    month: number,
    options?: { skipSharedMonthCache?: boolean },
  ): Observable<any> {
    const token = localStorage.getItem('token');
    const params = this.withPractices(new HttpParams()
      .set('year', year.toString())
      .set('month', month.toString())
      .set('groupId', this.groupId));

    return this.http.get<any[]>(`${this.apiUrl}/calendar/month`, {
      params,
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      tap((results: any) => {
        if (options?.skipSharedMonthCache) {
          return;
        }
        const appointments = results ? results.data : [];
        this.monthAppointmentSubject.next(appointments);
        this.setSelectedMonth(year, month);
      })
    );
  }

  loadAppointmentsForTodayAndUpcoming(groupId: string, page: number = 1, limit: number = 50): Observable<PaginatedResponse<any>> {
    const token = localStorage.getItem('token');
    const params = this.withPractices(new HttpParams()
      .set('groupId', groupId)
      .set('page', page.toString())
      .set('limit', limit.toString()));

    return this.http.get<any>(`${this.apiUrl}/calendar/upcoming`, {
      params,
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  private loadAllUpcomingAppointments(): Observable<any[]> {
    return this.loadAppointmentsForTodayAndUpcoming(this.groupId, 1, this.pageSize).pipe(
      // keep calling the next page while the API says hasMore
      expand(resp =>
        resp.pagination.hasMore
          ? this.loadAppointmentsForTodayAndUpcoming(
            this.groupId,
            resp.pagination.currentPage + 1,
            this.pageSize
          )
          : EMPTY
      ),
      // pluck out each page’s data array
      map(resp => resp.data),
      // stitch them all into one big array
      reduce<any[], any[]>((all, pageData) => all.concat(pageData), [])
    );
  }

  loadAppointmentsByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  checkAppointmentSlotAvailability(
    startDate: string,
    endDate: string,
    timeSlot: string,
    endSlot: string,
    groupId: string,
    id: string,
    physicianId?: string | null,
    kind: 'appointment' | 'event' = 'appointment',
    event?: { isAllDay?: boolean; recurrence?: any },
  ): Observable<any> {
    const token = localStorage.getItem('token');
    let params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate)
      .set('timeSlot', timeSlot)
      .set('endSlot', endSlot)
      .set('groupId', groupId)
      .set('id', id)
      .set('kind', kind);
    if (physicianId != null && String(physicianId).trim() !== '') {
      params = params.set('physicianId', String(physicianId).trim());
    }
    // A new event is checked over every day it would hold, including its repeats.
    if (event?.isAllDay) params = params.set('isAllDay', 'true');
    if (event?.recurrence) params = params.set('recurrence', JSON.stringify(event.recurrence));

    return this.http.get<any>(`${this.apiUrl}/check-availability`, {
      params,
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * Bookings/events already in an existing appointment's slot, excluding itself. Used when a
   * status change (un-cancel, un-DNA) puts a booking back into its slot.
   */
  findSlotConflictsFor(appointment: any, calendarSplitByPhysician: boolean): Observable<any[]> {
    return this.checkAppointmentSlotAvailability(
      moment(appointment.date).format('YYYY-MM-DD'),
      moment(appointment.date).format('YYYY-MM-DD'),
      appointment.timeSlot,
      appointment.endSlot,
      appointment.groupId || this.groupId,
      appointment.id,
      calendarSplitByPhysician ? (appointment.physicianId || null) : undefined,
    ).pipe(map((response: any) => response?.data || []));
  }

  sendAppointmentReminder(patient: any, appointment: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/send-reminder`, { patient, appointment }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  bookAppointment(appointmentData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(this.apiUrl, appointmentData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getDefaultConfirmationMessage(group: any): Observable<string> {
    const params = new HttpParams().set('group', JSON.stringify(group));
    return this.http.get<{ html: string }>(`${environment.API_URL}/api/default-confirmation-message`, { params }).pipe(
      map((response) => response.html)
    );
  }

  getAppointmentById(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateAppointment(id: string, appointmentData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/${id}`, appointmentData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  markAppointmentsAsStarted(patientId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/mark-started`, {
      patientId
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /** The recording finished: the server sets the end time on today's appointments (else yesterday's) that have none. */
  markAppointmentsAsEnded(patientId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/mark-ended`, { patientId }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  markAppointmentsAsCompleted(patientId: string, aiRequestCreatedAt?: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/mark-completed`, {
      patientId,
      aiRequestCreatedAt
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  deleteAppointment(id: string, body?: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.request('delete', `${this.apiUrl}/${id}`, {
      body: body,
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  setEditingAppointment(appointment: any | null) {
    this.editingAppointmentSubject.next(appointment);
  }

  setSelectedMonth(year: number, month: number) {
    this.selectedMonthSubject.next({ year, month });
  }

  compileDefaultAppointmentMessage(template: string, data: any) {
    return template.replace(/<([^>]+)>/g, (match, placeholder) => {
      return data[placeholder] || '';
    });
  }

  /**
   * Export a day's appointments to Excel (Azure Function generates file, streamed back).
   * @param groupId - Group ID
   * @param date - Date (YYYY-MM-DD or Date)
   * @param appointmentTypeNames - Optional list of appointment type names to include
   */
  exportDayAppointmentsToExcel(
    groupId: string,
    date: string | Date,
    appointmentTypeNames?: string[]
  ): Observable<Blob> {
    const token = localStorage.getItem('token');
    const dateStr = typeof date === 'string' ? date : (date as Date).toISOString().slice(0, 10);
    return this.http.post(
      `api/patients/group/${groupId}/appointments/export-day`,
      { date: dateStr, appointmentTypeNames },
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      }
    ) as Observable<Blob>;
  }

  /**
   * §3b — the theatre list, derived live from the day's `isProcedure` appointments. There is no
   * stored list to read: the document IS the query. A `.docx` for `'netcare'`, a `.pdf` for
   * `'daylist'` — the method name predates the second format and is kept so its callers do not
   * churn.
   *
   * `responseType: 'blob'` for the same reason as the Excel export above — this is a file, not
   * JSON, and an error body comes back as a Blob too (the caller's error handler must cope with
   * that; it falls back to a generic message rather than reading `error.message` off a Blob).
   *
   * @param documentType which renderer — the Netcare booking form or the VeriClaim day list.
   * @param appointmentTypeNames restrict to these procedure types; omit for every procedure.
   * @param physicianId  one surgeon's slate on a shared day.
   * @param hospitalId   one hospital, when the day spans two.
   */
  exportTheatreListToWord(
    groupId: string,
    date: string | Date,
    documentType: TheatreListDocumentType,
    appointmentTypeNames?: string[],
    physicianId?: string | null,
    hospitalId?: string | null,
  ): Observable<HttpResponse<Blob>> {
    const token = localStorage.getItem('token');
    const dateStr = typeof date === 'string' ? date : (date as Date).toISOString().slice(0, 10);
    // `observe: 'response'` — 2026-09-03, so the caller can read the filename out of
    // `Content-Disposition` instead of rebuilding it.
    //
    // It USED to return a bare Blob, and `schedule-calendar` derived the download name itself from
    // a hard-coded copy of the server's stem. That duplication was documented as "must move
    // together", and it did not: the stem was renamed twice in three days. Adding the practice
    // name to it would have meant a SECOND copy — of a slug function with rules about non-ASCII,
    // punctuation and length that would silently disagree with the server's the first time
    // somebody named their practice with an ampersand.
    //
    // Same-origin (`api/...` is relative), so no `Access-Control-Expose-Headers` is needed to read
    // it. The caller still keeps a fallback: a proxy that strips the header must not break the
    // download.
    return this.http.post(
      `api/patients/group/${groupId}/appointments/export-theatre-list`,
      { date: dateStr, documentType, appointmentTypeNames, physicianId, hospitalId },
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        observe: 'response'
      }
    ) as Observable<HttpResponse<Blob>>;
  }

  /**
   * The filename the server put in `Content-Disposition`, or '' when it is unreadable.
   *
   * Quoted or bare, and `filename*` (RFC 5987) preferred when present. Anything with a path
   * separator in it is REFUSED rather than sanitised — the value comes off the network, it is
   * handed to a download, and a caller that wanted a filename should not silently accept a path.
   */
  filenameFromResponse(response: HttpResponse<unknown> | null | undefined): string {
    const disposition = response?.headers?.get('Content-Disposition') || '';
    const extended = /filename\*\s*=\s*(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
    const raw = (extended?.[1] || plain?.[1] || '').trim();
    if (!raw) return '';
    let decoded = raw;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      // A stray '%' is not an encoding — keep what the header actually said.
    }
    return /[\/]/.test(decoded) ? '' : decoded;
  }
}